import { memo, useCallback, useState } from 'react';
import DefaultProperties from './DefaultProperties';
import Footer from './Footer';
import Title from './Title';
import { TruncateMultiline } from 'foundations-components/transitional/components/TruncateMultiline';
import { getProperty, getId } from 'customer-data-objects/objects/ObjectAccessors';
import styled from 'styled-components';
import CompactProperties from './CompactProperties';
import { NextLastActivity } from './NextLastActivity';
import { DEAL_TYPE_ID, LEAD_TYPE_ID, TASK_TYPE_ID, TICKET_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import TaskDueDateProperty from './TaskDueDateProperty';
import TicketTimeOpenProperty from './TicketTimeOpenProperty';
import Tags from './Tags';
import { ApprovalStatus } from './ApprovalStatus';
import { APPROVAL_COLUMN_TYPE } from '../constants';
import { HR } from 'foundations-components/transitional/components/HR';
import AssociationCount from './AssociationCount';
import { Popover } from 'foundations-components/transitional/components/Popover';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const MAX_TOOLTIP_CHARS = 144;
const StyledTitleContainer = styled.div.withConfig({
  displayName: "Card__StyledTitleContainer",
  componentId: "sc-1gdql3l-0"
})(["display:flex;justify-content:space-between;"]);
const StyledHoverContainer = styled.div.withConfig({
  displayName: "Card__StyledHoverContainer",
  componentId: "sc-1gdql3l-1"
})(["[data-test-id^='checkbox-select-card-']{transition:display 0ms;display:", ";}&:hover{[data-test-id^='checkbox-select-card-']{display:inline-block;}}"], ({
  boardBulkActionsVisible
}) => boardBulkActionsVisible ? 'inline-block' : 'none');
const getTooltipPropertyValue = (subject, options) => {
  if (!options.tooltipProperty) {
    return null;
  }
  const tooltipValue = getProperty(subject, options.tooltipProperty.name);
  if (!tooltipValue) {
    return null;
  }
  return tooltipValue;
};
const getPropertiesToDisplay = (subject, options) => {
  if (!options.secondaryProperties) {
    return [];
  }
  return options.secondaryProperties.reduce((accumulator, property) => {
    const value = getProperty(subject, property.name);
    if (value) {
      accumulator.push({
        definition: property,
        value
      });
    }
    return accumulator;
  }, []);
};
const CrmObjectCard = /*#__PURE__*/memo(({
  data,
  options,
  SelectionCheckbox,
  boardBulkActionsVisible
}) => {
  const {
    primaryAssociationDefinition,
    secondaryAssociationDefinition,
    compact,
    getAssociationData,
    showFooter,
    associationsForCurrentType,
    isInactive,
    isNextLastActivityTurnedOn,
    isInColumnConsideredComplete,
    columnType,
    isDealSplitsEnabled,
    currencyFormatOptions,
    onNavigateToRecordPage,
    tags,
    getIsUngated,
    onTrackCardActions: onTrack,
    titleProperty
  } = options;
  const cardId = String(data.object.objectId);
  const objectTypeId = data.objectTypeDefinition.objectTypeId;
  const showTags = !!tags;
  const showNextLastActivity = !isInColumnConsideredComplete && (objectTypeId === DEAL_TYPE_ID || objectTypeId === TICKET_TYPE_ID) && associationsForCurrentType && (isInactive || isNextLastActivityTurnedOn);
  const tooltipPropertyValue = getTooltipPropertyValue(data.object, options);
  const TooltipContent = useCallback(() => {
    if (!tooltipPropertyValue) {
      return null;
    }
    return /*#__PURE__*/_jsx(View, {
      minBlockSize: "50px",
      padding: "300",
      maxInlineSize: "250px",
      children: /*#__PURE__*/_jsx(TruncateMultiline, {
        maxChars: MAX_TOOLTIP_CHARS,
        text: tooltipPropertyValue,
        Paragraph: "span"
      })
    });
  }, [tooltipPropertyValue]);
  const propertiesToDisplay = getPropertiesToDisplay(data.object, options);
  const showApprovalStatus = columnType === APPROVAL_COLUMN_TYPE;
  const showAssociationCount = objectTypeId === TASK_TYPE_ID || objectTypeId === LEAD_TYPE_ID;
  const [hovered, setHovered] = useState(false);
  const handleMouseEnter = useCallback(() => {
    setHovered(true);
  }, []);
  const handleMouseLeave = useCallback(() => {
    setHovered(false);
  }, []);

  /* @bend-expect trellis-tokenized */
  return /*#__PURE__*/_jsx(Popover, {
    use: "longform",
    open: Boolean(tooltipPropertyValue) && hovered,
    placement: "right",
    content: {
      body: /*#__PURE__*/_jsx(TooltipContent, {})
    },
    children: /*#__PURE__*/_jsxs(StyledHoverContainer, {
      "data-test-id": "cdbc-card",
      "data-selenium-id": data.object.objectId,
      "data-selenium-info": showFooter,
      boardBulkActionsVisible: boardBulkActionsVisible,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      children: [/*#__PURE__*/_jsxs(StyledTitleContainer, {
        children: [/*#__PURE__*/_jsx(Title, {
          subject: data.object,
          objectTypeDefinition: data.objectTypeDefinition,
          isDealSplitsEnabled: isDealSplitsEnabled,
          onNavigateToRecordPage: onNavigateToRecordPage,
          recordHref: options.recordHref,
          titleProperty: titleProperty,
          getIsUngated: getIsUngated
        }), SelectionCheckbox && /*#__PURE__*/_jsx(SelectionCheckbox, {
          cardId: cardId
        })]
      }), objectTypeId === TICKET_TYPE_ID && /*#__PURE__*/_jsx(TicketTimeOpenProperty, {
        subject: data.object
      }), objectTypeId === TASK_TYPE_ID && /*#__PURE__*/_jsx(TaskDueDateProperty, {
        subject: data.object
      }), compact && /*#__PURE__*/_jsx(CompactProperties, {
        object: data.object,
        objectTypeDefinition: data.objectTypeDefinition,
        items: propertiesToDisplay,
        getIsUngated: getIsUngated,
        currencyFormatOptions: currencyFormatOptions
      }), !compact && /*#__PURE__*/_jsx(DefaultProperties, {
        object: data.object,
        objectTypeDefinition: data.objectTypeDefinition,
        items: propertiesToDisplay,
        getIsUngated: getIsUngated,
        currencyFormatOptions: currencyFormatOptions
      }), (showTags || showFooter || showNextLastActivity) && /*#__PURE__*/_jsx(HR, {
        distance: "flush",
        marginBlockStart: "200",
        marginBlockEnd: "100"
      }), showTags && /*#__PURE__*/_jsx(Tags, {
        tags: tags,
        isCompact: !!compact,
        object: data.object,
        objectTypeDef: data.objectTypeDefinition
      }), showFooter && /*#__PURE__*/_jsx(Footer, {
        subject: data.object,
        primaryAssociationDefinition: primaryAssociationDefinition,
        secondaryAssociationDefinition: secondaryAssociationDefinition,
        getAssociationData: getAssociationData
      }), showNextLastActivity && /*#__PURE__*/_jsx(NextLastActivity, {
        isNextLastActivityTurnedOn: Boolean(isNextLastActivityTurnedOn),
        isInactive: Boolean(isInactive),
        notesNextActivityDate: getProperty(data.object, 'notes_next_activity_date'),
        notesLastUpdated: getProperty(data.object, 'notes_last_updated'),
        objectId: getId(objectTypeId, data.object),
        createDate: getProperty(data.object, 'createdate'),
        associationsForCurrentType: associationsForCurrentType,
        objectTypeId: objectTypeId,
        getAssociationData: getAssociationData
      }), showApprovalStatus && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(HR, {
          distance: "flush",
          marginBlockStart: "200",
          marginBlockEnd: "200"
        }), /*#__PURE__*/_jsx(ApprovalStatus, {
          subject: data.object,
          objectTypeId: objectTypeId,
          onTrack: onTrack
        })]
      }), showAssociationCount && /*#__PURE__*/_jsx(AssociationCount, {
        subject: data.object,
        primaryAssociationDefinition: primaryAssociationDefinition,
        secondaryAssociationDefinition: secondaryAssociationDefinition,
        getAssociationData: getAssociationData
      })]
    })
  });
});
export default CrmObjectCard;